import { getScopeClauseCountWithOverlaps, IHighlightOverlap } from "@/utils";
import {
    Chip,
    Stack,
    styled,
    Tooltip,
    tooltipClasses,
    TooltipProps,
    Typography,
} from "@mui/material";
import { ReactElement } from "react";
import { IScopeClauseWithTopic } from "../../types";

/**
 * A component that displays the overlapping clauses count when hovering over a highlight.
 */
const OverlappingTitle = ({
    overlaps,
    allScopeClauses,
}: {
    overlaps: IHighlightOverlap[];
    allScopeClauses: IScopeClauseWithTopic[];
}) => {
    // Calculate total count: unique scope clauses at this location + additional overlapping clauses
    const scopeClauseCount = allScopeClauses.length;
    const overlapCount = getScopeClauseCountWithOverlaps(overlaps);

    // If we have multiple scope clauses at same location (same clause, multiple topics)
    // but no traditional overlaps, show the scope clause count
    // Otherwise, show the overlap count which may be higher
    const totalCount = Math.max(scopeClauseCount, overlapCount);

    return (
        <>
            <Typography>
                {totalCount} Clause{totalCount !== 1 ? "s" : ""}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Click to expand
            </Typography>
        </>
    );
};

/**
 * A component that displays the clause when hovering over a highlight.
 */
const ClauseTitle = ({
    scopeClause,
}: {
    scopeClause: IScopeClauseWithTopic;
}) => {
    return (
        <>
            <Typography
                color="primary"
                gutterBottom
                sx={{
                    textDecoration: "underline",
                }}
                fontWeight={"bold"}
                variant="body2"
            >
                {scopeClause.clause.title}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap={"wrap"}>
                {/* Policy Type Label */}
                <Chip
                    label={scopeClause.clause.policy_type.short_name}
                    size={"small"}
                />

                {/* Clause Types */}
                {scopeClause.clause.clause_types.map(({ name }, index) => (
                    <Chip
                        key={`${name}-${index}`}
                        label={name}
                        size={"small"}
                        color={"tertiary"}
                    />
                ))}

                {/* Topic Name (for V2 scopes) */}
                {scopeClause.scopeTopicName && (
                    <Chip
                        label={scopeClause.scopeTopicName}
                        size={"small"}
                        color="secondary"
                    />
                )}
            </Stack>
        </>
    );
};

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        boxShadow: theme.shadows[3],
        padding: theme.spacing(1.5, 2),
    },
}));

interface IProps {
    /**
     * The overlapping clauses (from different clause IDs that spatially overlap)
     */
    overlaps: IHighlightOverlap[];
    /**
     * The primary scope clause for this highlight
     */
    scopeClause: IScopeClauseWithTopic;
    /**
     * All scope clauses at this exact location (for same-clause-multiple-topics)
     */
    allScopeClauses: IScopeClauseWithTopic[];
    /**
     * The children element to wrap with tooltip
     */
    children: ReactElement;
}

/**
 * A tooltip that displays the clause or overlapping clauses count when hovering over a highlight.
 */
export const HighlightTooltip = ({
    overlaps,
    scopeClause,
    allScopeClauses,
    children,
}: IProps) => {
    // Show overlapping title if there are traditional overlaps OR multiple scope clauses at same location
    const hasMultipleClauses =
        overlaps.length > 0 || allScopeClauses.length > 1;

    return (
        <HtmlTooltip
            title={
                hasMultipleClauses ? (
                    <OverlappingTitle
                        overlaps={overlaps}
                        allScopeClauses={allScopeClauses}
                    />
                ) : (
                    <ClauseTitle scopeClause={scopeClause} />
                )
            }
            leaveDelay={100}
            followCursor
        >
            {children}
        </HtmlTooltip>
    );
};
